/*
 * Cân bằng số — thay file Excel "Theo dõi công việc TVOnline · SỐ T10" khách đang theo dõi tay (họp 30/10):
 * mỗi ngày mỗi telesale nhận bao nhiêu số theo từng nhóm dịch vụ; tổng, tỉ lệ thực tế so với tỉ lệ yêu cầu, số bù;
 * số phá (gọi mãi không liên lạc được) được bù thêm; booking tháng này so với tháng trước.
 */
import React, { useMemo, useState } from 'react';
import { DatePicker, Segmented, Tag, Tooltip } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { AGENTS, type Agent } from './data';
import type { Ctx } from './logic';
import { Card } from './ui';
import { GROUPS, groupOfMang, useGroupCfg, type GroupKey } from '../lead/groups';

/** Số phá: đã gọi từ 2 lần mà chưa lần nào liên lạc được */
const BROKEN_CALLS = 2;

interface Col { g: GroupKey; a: Agent }

export default function BalanceView({ ctx }: { ctx: Ctx }) {
  const navigate = useNavigate();
  const cfg = useGroupCfg((s) => s.cfg);
  const [month, setMonth] = useState<Dayjs>(dayjs(ctx.now).startOf('month'));
  const [gf, setGf] = useState<GroupKey | 'all'>('all');
  const from = month.startOf('month').valueOf();
  const to = month.endOf('month').valueOf();
  const days = Math.min(month.daysInMonth(), month.isSame(ctx.now, 'month') ? dayjs(ctx.now).date() : month.daysInMonth());

  const data = useMemo(() => {
    const leads = ctx.m.leads.filter((l) => l.createdAt >= from && l.createdAt <= to);
    // Nhóm của telesale: cài đặt; chưa cài thì theo mảng mặc định của nhân viên (chưa phân luồng = chỉ hiện ở nhóm có nhận số)
    const groupsOf = (a: Agent): GroupKey[] => cfg.staff[a.id]?.length ? cfg.staff[a.id] : a.mang ? [groupOfMang(a.mang).key] : [];
    const cols: Col[] = GROUPS.flatMap((g) => AGENTS
      .filter((a) => groupsOf(a).includes(g.key) || leads.some((l) => l.ownerId === a.id && groupOfMang(l.mang).key === g.key))
      .map((a) => ({ g: g.key, a })));
    const cell: Record<string, number> = {};
    const key = (day: number, c: Col) => `${day}|${c.g}|${c.a.id}`;
    leads.forEach((l) => {
      const k = key(dayjs(l.createdAt).date(), { g: groupOfMang(l.mang).key, a: AGENTS.find((a) => a.id === l.ownerId)! });
      cell[k] = (cell[k] ?? 0) + 1;
    });
    const total = (c: Col) => leads.filter((l) => l.ownerId === c.a.id && groupOfMang(l.mang).key === c.g).length;
    const gTotal = (g: GroupKey) => leads.filter((l) => groupOfMang(l.mang).key === g).length;
    // Tỉ lệ yêu cầu: cài đặt; chưa cài thì chia đều trong nhóm
    const want = (c: Col) => {
      const peers = cols.filter((x) => x.g === c.g);
      const set = cfg.ratio[c.a.id];
      return set ? set / 100 : 1 / Math.max(1, peers.length);
    };
    const callsBy = new Map<string, { n: number; ok: boolean }>();
    ctx.m.calls.forEach((x) => {
      if (!x.leadId || x.dir !== 'out') return;
      const cur = callsBy.get(x.leadId) ?? { n: 0, ok: false };
      callsBy.set(x.leadId, { n: cur.n + 1, ok: cur.ok || x.ok });
    });
    const broken = (c: Col) => leads.filter((l) => l.ownerId === c.a.id && groupOfMang(l.mang).key === c.g && !l.connectAt && (callsBy.get(l.id)?.n ?? 0) >= BROKEN_CALLS && !callsBy.get(l.id)?.ok).length;
    const book = (a: Agent, m: Dayjs) => ctx.m.appts.filter((x) => x.bookedBy === a.id && dayjs(x.bookedAt).isSame(m, 'month')).length;
    return { cols, cell, key, total, gTotal, want, broken, book };
  }, [ctx, cfg, from, to]);

  const cols = data.cols.filter((c) => gf === 'all' || c.g === gf);
  const sum = (c: Col) => data.total(c);
  const bu = (c: Col) => Math.round(data.gTotal(c.g) * data.want(c) - sum(c)) + data.broken(c);
  const prev = month.subtract(1, 'month');
  const head = 'sticky top-0 z-10 bg-gray-50 dark:bg-gray-700';
  const tint = (a: Agent) => ({ background: `${a.color}14` });

  return (
    <div className="space-y-4">
      <Card title="Cân bằng số theo nhóm dịch vụ"
        extra={(
          <span className="flex flex-wrap items-center gap-2">
            <Segmented size="small" value={gf} onChange={(x) => setGf(x as typeof gf)} options={[{ value: 'all', label: 'Tất cả nhóm' }, ...GROUPS.map((g) => ({ value: g.key, label: g.label }))]} />
            <DatePicker picker="month" size="small" value={month} onChange={(d) => d && setMonth(d.startOf('month'))} allowClear={false} format="MM/YYYY" />
            <button type="button" className="text-xs text-[#3e91ab] hover:underline" onClick={() => navigate('/telesales/settings?tab=groups')}>Cài nhóm, tỉ lệ</button>
          </span>
        )}>
        <div className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full border-collapse text-[12.5px] tabular-nums">
            <thead>
              <tr>
                <th className={`${head} sticky left-0 z-20 min-w-[110px] border-b border-r border-gray-200 px-2 py-1.5 text-left dark:border-gray-600`} rowSpan={2}>Ngày</th>
                {GROUPS.filter((g) => cols.some((c) => c.g === g.key)).map((g) => (
                  <th key={g.key} colSpan={cols.filter((c) => c.g === g.key).length} className={`${head} border-b border-r border-gray-200 px-2 py-1.5 text-center dark:border-gray-600`}>
                    <Tag color={g.color} className="!m-0 !font-semibold">{g.label}</Tag> <span className="text-xs font-normal text-gray-500">{data.gTotal(g.key)} số</span>
                  </th>
                ))}
              </tr>
              <tr>
                {cols.map((c) => (
                  <th key={`${c.g}${c.a.id}`} className={`${head} top-[33px] min-w-[84px] border-b border-r border-gray-200 px-2 py-1 text-center font-semibold dark:border-gray-600`} style={{ color: c.a.color }}>{c.a.name.split(' ').slice(-2).join(' ')}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Tổng', cls: 'font-bold', v: (c: Col) => sum(c) },
                { label: 'Tỉ lệ', cls: '', v: (c: Col) => `${data.gTotal(c.g) ? Math.round((sum(c) / data.gTotal(c.g)) * 1000) / 10 : 0}%` },
                { label: 'Tỉ lệ yêu cầu', cls: 'text-gray-500', v: (c: Col) => `${Math.round(data.want(c) * 1000) / 10}%` },
                { label: 'Số phá', cls: 'text-gray-500', v: (c: Col) => data.broken(c) || '—', tip: `Gọi từ ${BROKEN_CALLS} lần chưa liên lạc được — tính vào số bù` },
                { label: 'Bù', cls: 'font-bold', v: (c: Col) => { const b = bu(c); return <span className={b > 0 ? 'text-red-600' : b < 0 ? 'text-green-600' : ''}>{b > 0 ? `+${b}` : b}</span>; }, tip: 'Số nên chia thêm (đỏ) hoặc đang thừa (xanh) so với tỉ lệ yêu cầu, cộng số phá' },
              ].map((r) => (
                <tr key={r.label} className="bg-amber-50/70 dark:bg-amber-500/10">
                  <td className="sticky left-0 z-[5] border-b border-r border-gray-200 bg-amber-50 px-2 py-1 font-semibold dark:border-gray-600 dark:bg-gray-800">
                    {r.tip ? <Tooltip title={r.tip}><span className="cursor-help underline decoration-dotted">{r.label}</span></Tooltip> : r.label}
                  </td>
                  {cols.map((c) => <td key={`${c.g}${c.a.id}`} className={`border-b border-r border-gray-200 px-2 py-1 text-center dark:border-gray-600 ${r.cls}`}>{r.v(c)}</td>)}
                </tr>
              ))}
              {Array.from({ length: days }, (_, i) => days - i).map((d) => {
                const today = month.isSame(ctx.now, 'month') && d === dayjs(ctx.now).date();
                return (
                  <tr key={d} className={today ? 'font-semibold' : ''}>
                    <td className={`sticky left-0 z-[5] border-b border-r border-gray-200 px-2 py-1 dark:border-gray-600 ${today ? 'bg-[#3e91ab]/10' : 'bg-white dark:bg-gray-800'}`}>{month.date(d).format('DD/MM')}{today && ' · hôm nay'}</td>
                    {cols.map((c) => <td key={`${c.g}${c.a.id}`} className="border-b border-r border-gray-100 px-2 py-1 text-center dark:border-gray-700" style={tint(c.a)}>{data.cell[data.key(d, c)] ?? ''}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-gray-500">Ô đỏ ở dòng Bù = người đang thiếu số, chia data mới nên ưu tiên (bật “Ưu tiên bù số” trong Cài đặt thì hệ thống tự ưu tiên). Số liệu từ data đã chia cho từng telesale; dữ liệu Dashboard là dữ liệu mẫu, chờ nối API.</p>
      </Card>

      <Card title={`Booking tháng ${month.format('MM')} so với tháng ${prev.format('MM')}`}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {AGENTS.map((a) => {
            const n = data.book(a, month);
            const p = data.book(a, prev);
            return (
              <div key={a.id} className="rounded-lg border border-gray-200 p-2.5 dark:border-gray-700" style={tint(a)}>
                <div className="truncate text-[13px] font-semibold" style={{ color: a.color }}>{a.name}</div>
                <div className="mt-1 text-lg font-bold text-gray-800 dark:text-gray-100">{n} <span className="text-xs font-normal text-gray-500">lịch</span></div>
                <div className={`text-xs ${n - p >= 0 ? 'text-green-600' : 'text-red-600'}`}>{n - p >= 0 ? '+' : ''}{n - p} so với T{prev.format('M')} ({p})</div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
